/**
 * =========================================================================================
 * Activity Card Component - Individual activity item in the timeline
 * =========================================================================================
 */

import React, { useState } from 'react';
import * as Icon from 'ubs-icons';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UTags from 'ubs-framework/v2/UTags';
import { numberWithComaToFixed, getKeyName } from 'ubs-framework/v2/common/function';
import { dateFormatter } from 'ubs-framework/v2/common/function';
import UIcon from 'ubs-framework/v2/UIcon';
import { SUBSCRIPTION_STATUS_OPTIONS } from '../../../../../common/constant';

const ACTIVITY_COLORS = {
    CREATE: '#007AFF',
    UPGRADE: '#60B158',
    DOWNGRADE: '#FF6961',
    CANCEL: '#FF6961',
    PAUSE: '#FFA700',
    RESUME: '#60B158',
    PAYMENT_SUCCESS: '#60B158',
    PAYMENT_FAILED: '#FF6961',
    STATUS_CHANGE: '#007AFF',
    PAYMENT_METHOD_UPDATE: '#007AFF',
};

const ACTIVITY_TYPE_ICONS = {
    CREATE: Icon.Diamond,
    UPGRADE: Icon.UpArrow,
    DOWNGRADE: Icon.DownArrow,
    CANCEL: Icon.CloseMarkRound,
    PAUSE: Icon.Clock,
    RESUME: Icon.CheckMarkRound,
    PAYMENT_SUCCESS: Icon.CheckMarkRound,
    PAYMENT_FAILED: Icon.CloseMarkRound,
    STATUS_CHANGE: Icon.Reload,
    PAYMENT_METHOD_UPDATE: Icon.CreditCard,
};

const ACTIVITY_TYPE_LABELS = {
    CREATE: 'Created',
    UPGRADE: 'Upgraded',
    DOWNGRADE: 'Downgraded',
    CANCEL: 'Cancelled',
    PAUSE: 'Paused',
    RESUME: 'Resumed',
    PAYMENT_SUCCESS: 'Payment',
    PAYMENT_FAILED: 'Failed',
    STATUS_CHANGE: 'Changed',
    PAYMENT_METHOD_UPDATE: 'Updated',
};

const ActivityCard = React.memo(({ activity, isLast }) => {
    const [expanded, setExpanded] = useState(false);

    const color = ACTIVITY_COLORS[activity?.type] || '#007AFF';
    const TypeIcon = ACTIVITY_TYPE_ICONS[activity?.type] || Icon.InfoTooltip;
    const typeLabel = ACTIVITY_TYPE_LABELS[activity?.type] || activity?.type;
    const hasAmount = Boolean(activity?.amount && activity?.amount !== 0);
    const hasDescription = Boolean(activity?.description);

    const renderDetailRow = (label, value) => {
        if (!value && value !== 0) return null;
        return (
            <div className="u_flex-align u_py-6" key={label}>
                <span className="u_small-font u_color-font u_w-50">{label}</span>
                <span className="u_small-font u_color-title u_fw-600 u_flex-1-0">{value}</span>
            </div>
        );
    };

    const renderDetails = () => {
        if (!activity?.details) return null;
        const d = activity.details;
        const rows = [];

        if (d.previousStatus && d.newStatus) {
            const prevOption = getKeyName(SUBSCRIPTION_STATUS_OPTIONS, d.previousStatus);
            const newOption = getKeyName(SUBSCRIPTION_STATUS_OPTIONS, d.newStatus);
            rows.push(
                <div className="u_flex-align u_py-6" key="statusChange">
                    <span className="u_small-font u_color-font u_w-50">Status</span>
                    <div className="u_flex-align u_flex-1-0">
                        <UTags title={prevOption?.label || d.previousStatus} color={prevOption?.color || '#999999'} />
                        <Icon.RightArrow size={10} fill={COLOR.u_icon_bg} className="u_mx-8" />
                        <UTags title={newOption?.label || d.newStatus} color={newOption?.color || '#999999'} />
                    </div>
                </div>
            );
        }
        if (d.seatChange) rows.push(renderDetailRow('Seats', `${d.seatChange.from} → ${d.seatChange.to}`));
        if (d.planChange) rows.push(renderDetailRow('Plan', `${d.planChange.from} → ${d.planChange.to}`));
        if (d.amountChange) rows.push(renderDetailRow('Amount', `${numberWithComaToFixed(d.amountChange.from, activity?.currency)} → ${numberWithComaToFixed(d.amountChange.to, activity?.currency)}`));
        if (d.prorationAmount) rows.push(renderDetailRow('Proration', numberWithComaToFixed(d.prorationAmount, activity?.currency)));
        if (d.refundAmount) rows.push(renderDetailRow('Refund', numberWithComaToFixed(d.refundAmount, activity?.currency)));
        if (d.nextBillingDate) rows.push(renderDetailRow('Next Billing', dateFormatter({ date: d.nextBillingDate, format: 'DD MMM YYYY' })));
        if (d.gracePeriodEnd) rows.push(renderDetailRow('Grace Period', `Until ${dateFormatter({ date: d.gracePeriodEnd, format: 'DD MMM YYYY' })}`));
        if (d.accessUntil) rows.push(renderDetailRow('Access Until', dateFormatter({ date: d.accessUntil, format: 'DD MMM YYYY' })));
        if (d.failureReason) rows.push(renderDetailRow('Reason', d.failureReason));
        if (d.retryCount) rows.push(renderDetailRow('Retry Count', d.retryCount));
        if (d.reason) rows.push(renderDetailRow('Reason', d.reason));
        if (d.paymentId) rows.push(renderDetailRow('Payment ID', d.paymentId));
        if (d.orderId) rows.push(renderDetailRow('Order ID', d.orderId));

        return rows.filter(Boolean);
    };

    const detailRows = renderDetails();
    const canExpand = (detailRows && detailRows.length > 0) || hasDescription;

    return (
        <div className="u_flex">
            {/* Timeline dot + line */}
            <div className="u_flex u_direction-column u_align-center u_mr-16">
                <UIcon color={color} thumb={40} icon={<TypeIcon size={18} fill={color} />} type="square" />
                {!isLast && <div className="u_flex-1 u_timeline-connector" />}
            </div>

            {/* Card */}
            <div className="u_flex-1-0 u_pb-12">
                <div
                    className={`u_activity-card u_bg-white u_radius-8 u_b-light u_px-16 u_py-10 ${canExpand ? 'u_cp' : ''}`}
                    onClick={() => canExpand && setExpanded(!expanded)}
                >
                    {/* Single row: Badge | Title | Amount | Time | Arrow */}
                    <div className="u_flex-align u_content-between">
                        <div className="u_flex-align u_flex-1-0 u_mr-12">
                            <UTags title={typeLabel} color={color} className="u_flex-shrink u_mr-10" />
                            <span className="u_font-14 u_fw-600 u_color-title u_ellipse u_mr-8">{activity?.title}</span>
                            {hasAmount && (
                                <span className={`u_font-14 u_fw-700 u_ws-nowrap ${activity?.type === 'PAYMENT_FAILED' ? 'u_color-red' : 'u_color-green'}`}>
                                    {numberWithComaToFixed(activity.amount, activity?.currency)}
                                </span>
                            )}
                        </div>
                        <div className="u_flex-align u_flex-shrink">
                            <span className="u_small-font u_color-font u_ws-nowrap">
                                {dateFormatter({ date: activity?.timestamp || activity?.createdAt, format: 'DD MMM YYYY, hh:mm A' })}
                            </span>
                            {canExpand && (
                                <div className={`u_detail-arrow u_ml-12 ${expanded ? 'u_rotated' : ''}`}>
                                    <Icon.DownArrow size={10} fill={COLOR.u_icon_bg} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Expandable: Description + Details */}
                    {canExpand && expanded && (
                        <div className="u_detail-content u_mt-10 u_pt-10 u_bt-light">
                            {hasDescription && (
                                <p className="u_small-font u_color-font u_mb-8">{activity.description}</p>
                            )}
                            {activity.category !== 'USER_ACTION' && (
                                <div className="u_flex-align u_py-6">
                                    <span className="u_small-font u_color-font u_w-50">Source</span>
                                    <UTags title="System" color="#8F95A6" />
                                </div>
                            )}
                            {detailRows}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default ActivityCard;
